import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

type AuthValue = {
  user: User | null
  session: Session | null
  profile: { full_name:string | null; role:string; active:boolean; establishment_id:string } | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<AuthValue['profile']>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    async function setAuth(next: Session | null) {
      setSession(next)
      if (!next?.user) { if (active) { setProfile(null); setLoading(false) }; return }
      const { data, error } = await supabase.from('profiles').select('full_name,role,active,establishment_id').eq('id', next.user.id).maybeSingle()
      if (!active) return
      if (error) console.error('Falha ao verificar permissões:', error.message)
      setProfile(data)
      setLoading(false)
    }

    supabase.auth.getSession().then(({ data, error }) => {
      if (error) console.error('Falha ao restaurar a sessão:', error.message)
      return setAuth(data.session)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return
      void setAuth(next)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthValue>(() => ({
    user: session?.user ?? null,
    session,
    profile,
    loading,
    signOut: async () => {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
  }), [session, profile, loading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider')
  return context
}
